import { DeleteOutlineOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { DateFormatPreset, useConfirmationDialog } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useRuleActions } from '../RuleDetail/hooks/useRuleActions/useRuleActions';
import { TsrctRuleStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  DataGridConfig,
  DataGridLinkVariant,
  LinkCell,
  LinkCellValue,
  UseDataGridConfig,
  cellFactory,
  gridColDefFactory,
  RuleStatusChipCell,
  ChipCell,
  joinFormatterFactory,
  dateFormatterFactory,
} from '~/components/DataGridCustom';
import { UserChipCell } from '~/components/DataGridCustom/cells/UserChipCell/UserChipCell.tsx';
import { curiosityValueGetter } from '~/components/DataGridWidget/DataGridWidget.util.tsx';
import { RouteId } from '~/routes/RouteId';

import { RuleListDataGridField, RuleDataGridRow } from './RuleListSourcingDataGrid.types';

export const useRuleDataGridConfig: UseDataGridConfig<any> = () => {
  const { t } = useTranslation();
  const { remove } = useRuleActions();
  const { confirm } = useConfirmationDialog();
  const { t: tColumn } = useTranslation(I18nNamespace.ruleBuilder, { keyPrefix: 'RuleDataGrid.column' });
  const { t: tAction } = useTranslation(I18nNamespace.ruleBuilder, { keyPrefix: 'RuleDataGrid.actions' });
  const { t: tRuleVariant } = useTranslation(I18nNamespace.ruleBuilder, { keyPrefix: 'RuleVariant' });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    return {
      sortModel: [{ field: RuleListDataGridField.SEQUENCE_NUMBER, sort: 'desc' }],
      columnVisibilityModel: {
        [RuleListDataGridField.CREATED_AT]: false,
        [RuleListDataGridField.UPDATED_AT]: false,
      },
      columns: [
        createColumn({
          field: RuleListDataGridField.SEQUENCE_NUMBER,
          renderCell: cellFactory(LinkCell),
          valueGetter: (value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.ruleDetail(row.id),
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 100,
          hideable: false,
        }),
        createColumn({
          field: RuleListDataGridField.STATUS,
          minWidth: 150,
          renderCell: cellFactory(RuleStatusChipCell),
        }),
        createColumn({
          field: RuleListDataGridField.NAME,
          minWidth: 300,
        }),
        createColumn({
          field: RuleListDataGridField.VARIANT,
          minWidth: 300,
          renderCell: cellFactory(ChipCell, ({ value }) => ({
            value: tRuleVariant(value),
          })),
        }),
        createColumn({
          field: RuleListDataGridField.COMMODITY_GROUP_IDS,
          renderCell: cellFactory(ChipCell, ({ value }) => ({
            value: curiosityValueGetter(CuriosityNamespaceType.COMMODITY_GROUP, value),
          })),
          valueFormatter: joinFormatterFactory(),
          sortable: false,
          minWidth: 200,
        }),
        createColumn({
          field: RuleListDataGridField.ORGANIZATION_IDS,
          renderCell: cellFactory(ChipCell, ({ value }) => ({
            value: curiosityValueGetter(CuriosityNamespaceType.ORGANIZATION, value),
          })),
          valueFormatter: joinFormatterFactory(),
          sortable: false,
          minWidth: 200,
        }),
        createColumn({
          field: RuleListDataGridField.SOURCE_SYSTEM_IDS,
          renderCell: cellFactory(ChipCell, ({ value }) => ({
            value: curiosityValueGetter(CuriosityNamespaceType.SOURCE_SYSTEM, value),
          })),
          valueFormatter: joinFormatterFactory(),
          minWidth: 200,
          sortable: false,
        }),
        createColumn({
          field: RuleListDataGridField.CREATED_AT,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 120,
        }),
        createColumn({
          field: RuleListDataGridField.UPDATED_AT,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 120,
        }),
        createColumn({
          field: RuleListDataGridField.RESPONSIBLE_IDS,
          renderCell: cellFactory(UserChipCell, { ns: CuriosityNamespaceType.INTERNAL_USER }),
          valueFormatter: joinFormatterFactory(),
          minWidth: 300,
          sortable: false,
        }),
        createColumn({
          field: RuleListDataGridField.ACTIONS,
          type: 'actions',
          getActions: (params) => [
            <Tooltip title={tAction('title')} arrow placement="top" key="delete">
              <GridActionsCellItem
                disabled={params.row.status === TsrctRuleStatus.PUBLISHED}
                icon={<DeleteOutlineOutlined />}
                label={tAction('delete')}
                onClick={confirm(
                  async () => {
                    await remove({ id: params.row.id });
                  },
                  {
                    title: tAction('delete.title'),
                    message: tAction('delete.message', { ruleName: params.row.name }),
                  },
                )}
              />
            </Tooltip>,
          ],
          sortable: false,
          hideable: false,
          resizable: false,
          minWidth: 50,
          align: 'left',
        }),
      ],
      exportModel: {
        [RuleListDataGridField.COMMODITY_GROUP_IDS]: {
          resolveCuriosity: CuriosityNamespaceType.COMMODITY_GROUP,
        },
        [RuleListDataGridField.ORGANIZATION_IDS]: {
          resolveCuriosity: CuriosityNamespaceType.ORGANIZATION,
        },
        [RuleListDataGridField.SOURCE_SYSTEM_IDS]: {
          resolveCuriosity: CuriosityNamespaceType.SOURCE_SYSTEM,
        },
        [RuleListDataGridField.RESPONSIBLE_IDS]: {
          resolveCuriosity: CuriosityNamespaceType.INTERNAL_USER,
        },
        [RuleListDataGridField.ACTIONS]: {
          disable: true,
        },
      },
    } as DataGridConfig<RuleDataGridRow>;
  }, [tColumn, t, tAction, tRuleVariant, confirm, remove]);
};
